import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { RebateDocumentLanguage, RebateDocumentType } from './rebate-document-view';

export interface IRebateDocumentPreview {
  bankId?: number;
  bankName?: string;
  accountNumber?: string;
  docType?: string;
  letterCategory?: string;
  language?: string;
  beneficiaryName?: string;
  referenceNo?: string;
  date?: string;
  periodLabel?: string;
  amount?: number;
  currency?: string;
  totalDeals?: number;
}

/**
 * Shared data-fetching for the three rebate print pages (rebate-invoice-print,
 * rebate-incentive-print, rebate-cancellation-print): calls GET /api/rebate-documents/preview
 * (ALSHMOKH-24), which resolves the real date/period/amount/currency/total-deals/reference number
 * from the bank's saved rebate schedule and letter category — the same way the scheduled email
 * does, except the reference number is only peeked, never reserved.
 * Reads `bankId` and `commodityType` from the query string: ?bankId=<id>&commodityType=Local|International
 *
 * The document's language (ALSHMOKH-31) follows the bank's saved rebate letter language rather than
 * the viewer's own session locale, so the preview matches what will actually be emailed.
 */
export const useRebateDocumentData = (docType: RebateDocumentType) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const bankIdParam = params.get('bankId');
  const commodityType = params.get('commodityType') === 'Local' ? 'Local' : 'International';

  const [preview, setPreview] = useState<IRebateDocumentPreview | undefined>(undefined);
  const language: RebateDocumentLanguage = preview?.language === 'AR' ? 'ar' : 'en';

  useEffect(() => {
    if (!bankIdParam) {
      return;
    }
    let cancelled = false;
    axios
      .get<IRebateDocumentPreview>(
        `/api/rebate-documents/preview?bankId=${bankIdParam}&commodityType=${commodityType}&docType=${docType}`
      )
      .then(res => {
        if (!cancelled) {
          setPreview(res.data);
        }
      })
      .catch(error => {
        console.error('Error loading rebate document preview:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [bankIdParam, commodityType, docType]);

  return {
    language,
    bankName: preview?.bankName || '',
    accountNumber: preview?.accountNumber || '',
    letterCategory: preview?.letterCategory,
    referenceNo: preview?.referenceNo || '',
    date: preview?.date || '',
    periodLabel: preview?.periodLabel || '',
    currency: preview?.currency || '',
    amount: preview?.amount ?? 0,
    totalDeals: preview?.totalDeals ?? 0,
  };
};

export default useRebateDocumentData;
